import { Component, type ErrorInfo, type JSX, type ReactNode } from "react";

import { Link, useLocation } from "react-router";

import { Button } from "@/components/ui/button";
import { buttonVariants } from "@/components/ui/button-variants";
import { Card, CardContent } from "@/components/ui/card";
import { CODE_SURFACE, EYEBROW } from "@/lib/class-presets";
import { cn } from "@/lib/utils";

function errorOf(value: unknown): Error {
  return value instanceof Error ? value : new Error(String(value));
}

/**
 * Pages load as separate chunks, so a page can fail for a reason that has
 * nothing to do with its code: a deploy replaced the hashed files under an open
 * session, or the network dropped the request. That failure is recoverable by
 * reloading, and saying so beats showing a module URL to an operator.
 */
export function isChunkLoadError(error: Error): boolean {
  const text = `${error.name} ${error.message}`.toLowerCase();
  return (
    text.includes("chunkloaderror") ||
    text.includes("dynamically imported module") ||
    text.includes("importing a module script failed") ||
    text.includes("error loading dynamically imported module")
  );
}

function reloadPage(): void {
  window.location.reload();
}

/** Token-styled stand-in for a route that threw while rendering. */
export function RouteErrorPanel({ error }: { readonly error: Error }): JSX.Element {
  const chunkFailure = isChunkLoadError(error);
  return (
    <div className="grid gap-4" role="alert">
      <header>
        <p className={EYEBROW}>Something went wrong</p>
        <h1>{chunkFailure ? "This page could not be loaded" : "This page failed to render"}</h1>
      </header>
      <Card size="sm">
        <CardContent className="grid gap-3">
          <p className="text-muted-foreground">
            {chunkFailure
              ? "Part of the app did not download. That usually means a new version shipped while this tab was open, or the connection dropped — reloading fetches the current files."
              : "The rest of the app still works. Reload the page, or head back to the dashboard and try again. If it keeps happening, send this message to the platform team."}
          </p>
          <pre className={cn(CODE_SURFACE, "max-h-48")}>{error.message}</pre>
          <div className="flex flex-wrap gap-2">
            {chunkFailure && <Button onClick={reloadPage}>Reload the page</Button>}
            <Link
              className={cn(buttonVariants({ variant: chunkFailure ? "outline" : "default" }))}
              to="/"
            >
              Back to dashboard
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * `ErrorBoundary` wired to the router. A boundary with no reset key latches: the
 * panel it renders stays up forever, so even its own "Back to dashboard" link
 * navigates without bringing the app back. `location.key` changes on every
 * navigation — including a repeat of the same path — so recovery always works.
 */
export function RoutedErrorBoundary({ children }: { readonly children: ReactNode }): JSX.Element {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.key}>{children}</ErrorBoundary>;
}

export interface ErrorBoundaryProps {
  /** Changing this clears a captured error — pass the pathname to reset on navigation. */
  readonly resetKey?: string;
  readonly children: ReactNode;
}

interface ErrorBoundaryState {
  readonly error: Error | null;
  readonly resetKey: string | undefined;
}

/**
 * React only catches render errors through a class component, so this is the one
 * class in the frontend. Without it a single throwing route unmounts the whole
 * tree and the browser shows a blank white page.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return { error: errorOf(error) };
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    if (props.resetKey === state.resetKey) return null;
    return { error: null, resetKey: props.resetKey };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error("Unhandled render error", errorOf(error), info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.error === null) return this.props.children;
    return <RouteErrorPanel error={this.state.error} />;
  }
}
